import React, { useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { 
  BookOpen, 
  ChevronRight, 
  X,
  Sparkles,
  Award,
  Globe,
  Info
} from "lucide-react";

interface LearningCard {
  id: string;
  title: string;
  category: string;
  description: string;
  iconBg: string;
  textColor: string;
  detailedContent: {
    heading: string;
    body: string;
  }[];
  renderIcon: () => React.ReactNode;
}

interface BlogSectionProps {
  navigate?: (path: string) => void;
}

export default function BlogSection({ navigate }: BlogSectionProps) {
  const [selectedCard, setSelectedCard] = useState<LearningCard | null>(null);

  const learningCards: LearningCard[] = [
    {
      id: "plant-care",
      title: "Plant Care",
      category: "Care Guidelines",
      description: "Learn watering, lighting, fertilization and healthy growth techniques.",
      iconBg: "bg-emerald-50 border-emerald-100/60",
      textColor: "text-emerald-800",
      renderIcon: () => (
        <svg className="w-7 h-7 text-emerald-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 3.5 1 9.8a7 7 0 0 1-9 8.2z" />
          <path d="M9 22v-4" />
          <path d="M14 14.5l-2.5-2.5" />
          <path d="M12 18.5l-2.5-2.5" />
        </svg>
      ),
      detailedContent: [
        {
          heading: "Hydration Science & Soil Mechanics",
          body: "Watering is the single most critical factor in houseplant longevity. Rather than watering on a strict calendar interval, rely on soil moisture indices. Inserting a finger 2 inches into the growing medium allows the assessment of moisture levels near the upper root layer. For tropical and arid species, wait for this zone to dry before drenching deeply."
        },
        {
          heading: "Lighting Profiles & Photosynthesis",
          body: "Plants require light waves to stimulate photosynthesis. Bright indirect light mimics a tropical forest canopy floor, providing 10,000 to 20,000 lux without burning tender leaves. Monitor foliage: yellowing can indicate inadequate lumens, whereas brown, crispy dry spots frequently trace back to direct UV foliage burn."
        },
        {
          heading: "Macronutrient Balancing",
          body: "During active spring and summer cycles, supplement with a balanced Nitrogen-Phosphorus-Potassium (NPK) liquid fertilizer diluted to half-strength. Nitrogen supports vegetative leafy vigor, Phosphorus promotes robust root development and blooming, and Potassium builds overall plant cell wall integrity."
        }
      ]
    },
    {
      id: "indoor-plants",
      title: "Indoor Plants",
      category: "Houseplant Mastery",
      description: "Master the art of keeping tropical species thriving in indoor environments.",
      iconBg: "bg-teal-50 border-teal-100/60",
      textColor: "text-teal-800",
      renderIcon: () => (
        <svg className="w-7 h-7 text-teal-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M7 11h10v4c0 1.5-1 3-3 3h-4c-2 0-3-1.5-3-3v-4z" />
          <path d="M5 11h14" />
          <path d="M12 2v5" />
          <path d="M12 5c-1.5-1.5-3-2-3-2s.5 2 1.5 2.5" />
          <path d="M12 5c1.5-1.5 3-2 3-2s-.5 2-1.5 2.5" />
          <path d="M10 8c-2-1-4-.5-4-.5s1.5 2 3 1.5" />
          <path d="M14 8c2-1 4-.5 4-.5s-1.5 2-3 1.5" />
        </svg>
      ),
      detailedContent: [
        {
          heading: "Microclimate Management",
          body: "Modern indoor environments often lack the ambient humidity tropical plants crave. Grouping plants together generates mutual moisture vapor through transpiration. Alternatively, place sensitive specimens on gravel-filled drainage trays containing shallow standing water to elevate the local relative humidity."
        },
        {
          heading: "Ideal Potting Substrates",
          body: "Commercial multi-purpose potting soils degrade and compact quickly, starving root zones of essential air. Mix standard indoor potting soils with 20% coarse perlite and 20% high-quality orchid bark. This open structural mesh supports high moisture capacity while encouraging water to drain freely."
        }
      ]
    },
    {
      id: "plant-identification",
      title: "Plant Identification",
      category: "Taxonomic Diagnostics",
      description: "Discover how to identify plants using vein patterns and flower morphology.",
      iconBg: "bg-blue-50 border-blue-100/60",
      textColor: "text-blue-800",
      renderIcon: () => (
        <svg className="w-7 h-7 text-blue-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="10" cy="10" r="5" />
          <path d="M13.5 13.5L19 19" />
          <path d="M8 8a2 2 0 0 1 2 2" />
          <path d="M18.5 7.5c1.5-.5 2.5-1.5 2.5-1.5s-1.5 .5-2.5 2c0 0-1.5 1-2 2" />
        </svg>
      ),
      detailedContent: [
        {
          heading: "Morphological Leaf Taxonomy",
          body: "Experienced botanists identify specimens based on structural attributes. Key parameters include leaf arrangement (opposite vs. alternate patterns), margin styles (serrated, entire, lobed), and venation layouts. Parallel veins typically point to monocots, whereas branched netting indicates dicots."
        },
        {
          heading: "Diagnostic Key Analysis",
          body: "Dichotomous keys present a sequence of physical choices leading to precise scientific categorizations. Observing details such as whether stems possess soft downy hair, or whether flowers exhibit radial symmetry, helps narrow classification lists confidently."
        }
      ]
    },
    {
      id: "plant-diseases",
      title: "Plant Diseases",
      category: "Organic Treatment",
      description: "Diagnose soil pathogens, fungal mildew, and nutrient deficiencies early.",
      iconBg: "bg-amber-50 border-amber-100/60",
      textColor: "text-amber-800",
      renderIcon: () => (
        <svg className="w-7 h-7 text-amber-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          <path d="M12 6c-1.5 1.5-2 3-2 3s1 1.5 2 2.5c1-1 2-2.5 2-2.5s-.5-1.5-2-3z" />
        </svg>
      ),
      detailedContent: [
        {
          heading: "Preventing Root Rot",
          body: "Saturated, airless soil fuels the expansion of dangerous fungal root rot. Affected plants lose stem elasticity, droop persistently, and exhibit dark, mushy roots. Treatment involves trimming structural rot, washing the root crown in diluted biological active fungicide, and repotting in sterile dry mix."
        },
        {
          heading: "Spotting Nutrient Shortages",
          body: "A plant's physical symptoms provide a direct window into soil nutrients. Interveinal chlorosis (yellow space between deep green veins) indicates iron or magnesium scarcity. Uniform yellowing of old bottom leaves usually signals nitrogen depletion, easily corrected with organic bio-composting."
        }
      ]
    },
    {
      id: "gardening-tips",
      title: "Gardening Tips",
      category: "Soil Preservation",
      description: "Practical techniques for soil preparation, companion planting, and harvesting.",
      iconBg: "bg-orange-50 border-orange-100/60",
      textColor: "text-orange-850",
      renderIcon: () => (
        <svg className="w-7 h-7 text-orange-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M14 2l4 4" />
          <path d="M15 3l-9 9" />
          <path d="M9 15l-3 3v2h2l3-3" />
          <path d="M18 6l3-3M6 18H3v3h3" />
        </svg>
      ),
      detailedContent: [
        {
          heading: "Soil Enrichment Principles",
          body: "Healthy gardens depend on active living soil. Introduce high-grade organic compost annually to restore beneficial microbiology. Mulching the surface with 2-3 inches of shredded straw, bark, or leaf mold helps lock in moisture, regulate temperature, and suppress weeds."
        },
        {
          heading: "Aeration & Compaction Prevention",
          body: "Avoid walking on garden beds, as compression squeezes vital air pockets out of the root zone. Broadfork or aerate your beds during early spring to break up tight clay without disrupting historical sub-layers."
        }
      ]
    },
    {
      id: "flowers",
      title: "Flowers",
      category: "Blooming Care",
      description: "Understand blooming cycles, pollination stages, and floral displays.",
      iconBg: "bg-rose-50 border-rose-100/60",
      textColor: "text-rose-800",
      renderIcon: () => (
        <svg className="w-7 h-7 text-rose-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="3" />
          <path d="M12 2a3 3 0 0 0-3 3c0 1.5 1 2.5 3 4 2-1.5 3-2.5 3-4a3 3 0 0 0-3-3z" />
          <path d="M12 22a3 3 0 0 0 3-3c0-1.5-1-2.5-3-4-2 1.5-3 2.5-3 4a3 3 0 0 0 3 3z" />
          <path d="M2 12a3 3 0 0 0 3 3c1.5 0 2.5-1 4-3-1.5-2-2.5-3-4-3a3 3 0 0 0-3 3z" />
          <path d="M22 12a3 3 0 0 0-3-3c-1.5 0-2.5 1-4 3 1.5 2 2.5 3 4 3a3 3 0 0 0 3-3z" />
        </svg>
      ),
      detailedContent: [
        {
          heading: "Deadheading & Bloom Production",
          body: "Pruning faded flowers (deadheading) diverts energy away from seed production and channels it back into fresh flower clusters. For heavy feeders like roses and dahlias, apply high-phosphorus fertilizers to support continuous blooms."
        },
        {
          heading: "Supporting Local Pollinators",
          body: "Cultivating host plants and tubular, pollinator-friendly blossoms supports regional bee and butterfly networks. Grouping diverse, native floral colors together across your landscape helps local clean-air wildlife locate pollen sources easily."
        }
      ]
    },
    {
      id: "trees",
      title: "Trees",
      category: "Dendrology Hub",
      description: "Guide to selecting, pruning, and fostering robust tree health.",
      iconBg: "bg-emerald-50 border-emerald-100/60",
      textColor: "text-emerald-900",
      renderIcon: () => (
        <svg className="w-7 h-7 text-emerald-700" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M12 2L3 17h18L12 2z" />
          <path d="M12 12L6 21h12L12 12z" />
          <path d="M12 18v4" />
        </svg>
      ),
      detailedContent: [
        {
          heading: "Proper Pruning Cuts",
          body: "To keep trees structurally sound, make clean cuts just outside the branch collar—never leave long stubs or cut flush against the trunk. Pruning during winter dormancy minimizes pathogen transmission and sap bleeding."
        },
        {
          heading: "Sapling Establishment Techniques",
          body: "Dig tree-planting holes twice as wide as the root ball, keeping the depth identical so the root flare remains level with the surrounding soil. Deep, slow-drip watering encourages young roots to reach deep into the earth."
        }
      ]
    },
    {
      id: "succulents",
      title: "Succulents",
      category: "Xeric Species",
      description: "Grow succulents successfully using dry mineral soils and solar exposure.",
      iconBg: "bg-teal-50 border-teal-100/60",
      textColor: "text-teal-900",
      renderIcon: () => (
        <svg className="w-7 h-7 text-emerald-800" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M12 2v20" />
          <path d="M12 8c2.5 0 4.5 2 4.5 4.5v3.5a1 1 0 0 0 2 0v-3.5c0-3.5-3-6.5-6.5-6.5" />
          <path d="M12 11c-2.5 0-4.5 2-4.5 4.5v1.5a1 1 0 0 1-2 0v-1.5c0-3.5 3-6.5 6.5-6.5" />
        </svg>
      ),
      detailedContent: [
        {
          heading: "The Soak & Dry Protocol",
          body: "Desert species store moisture in fleshy stems and leaves to survive dry spells. Overwatering triggers rapid rot. Always let the soil dry out fully before drenching again, ensuring all excess water drains away completely through bottom drainage."
        },
        {
          heading: "Mineral Soil Formulation",
          body: "Avoid standard peat-rich soils that retain moisture too long. Mix 2 parts coarse perlite or pumice with 1 part garden soil. This loose blend allows roots to breathe and prevents rot."
        }
      ]
    },
    {
      id: "herbs",
      title: "Herbs",
      category: "Herbology Tips",
      description: "Propagate, harvest, and preserve fresh aromatic and culinary herbs.",
      iconBg: "bg-lime-50 border-lime-100/60",
      textColor: "text-lime-850",
      renderIcon: () => (
        <svg className="w-7 h-7 text-lime-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M12 22V2" />
          <path d="M12 6c1.5-1.5 4-2 6-2s2.5 1 2.5 2.5-1 3-3 3H12" />
          <path d="M12 11c-1.5-1.5-4-2-6-2s-2.5 1-2.5 2.5 1 3 3 3h5" />
          <path d="M12 16c2-1.5 4.5-2 6-2s2 1 2 2.5-1 2-2.5 2H12" />
        </svg>
      ),
      detailedContent: [
        {
          heading: "Regular Pinching & Bushiness",
          body: "Pinching off the growing tips of basil, mint, and oregano keeps them dense and bushy, preventing leggy growth. Trimming before flowering preserves structural aromatic oil content in the leaves."
        },
        {
          heading: "Optimal Drying & Preservation",
          body: "Hang fresh herbs upside down in a warm, dry room out of direct sunlight. Once dry, store them in airtight jars to keep their culinary and herbal qualities intact."
        }
      ]
    },
    {
      id: "botanical-science",
      title: "Botanical Science",
      category: "Phytological Studies",
      description: "Explore photosynthesis pathways, vascular structures, and genetics.",
      iconBg: "bg-indigo-50 border-indigo-100/60",
      textColor: "text-indigo-850",
      renderIcon: () => (
        <svg className="w-7 h-7 text-indigo-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M4.5 16.5C7.5 10.5 10 8 13.5 8" />
          <path d="M19.5 7.5a2.5 2.5 0 0 0-2.5-2.5" />
          <path d="M13.5 8c2.5-2.5 3-4.5 3-4.5s-2 1-3.5 3.5" />
          <path d="M14 12c-2.5 2.5-3.5 4.5-3.5 4.5s2-1 3.5-3.5" />
          <rect x="15" y="15" width="6" height="6" rx="1" />
          <path d="M9 19c0-4.5 3.5-8 8-8" />
        </svg>
      ),
      detailedContent: [
        {
          heading: "Vascular Transport Mechanisms",
          body: "Water and dissolved soil minerals travel upward from root systems inside specialized vascular conduits called xylem vessels. The driving force behind water transport is transpiration, which pulls liquid steadily toward leaves."
        },
        {
          heading: "Photosynthetic Carbon Fixation",
          body: "Inside green chloroplasts, light energy is captured by chlorophyll molecules of photosystems. This energy transforms atmospheric CO2 and irrigation water into simple sugars, and releases oxygen into the environment."
        }
      ]
    },
    {
      id: "hydroponics",
      title: "Hydroponics",
      category: "Soilless Growth",
      description: "Learn how to cultivate foliage using nutrient-enriched water circuits.",
      iconBg: "bg-sky-50 border-sky-100/60",
      textColor: "text-sky-850",
      renderIcon: () => (
        <svg className="w-7 h-7 text-sky-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
          <path d="M12 6a2 2 0 1 1 0-4 2 2 0 0 1 0 4z" />
          <circle cx="12" cy="18" r="1.5" />
        </svg>
      ),
      detailedContent: [
        {
          heading: "Nutrient Film Techniques",
          body: "In active hydroponic structures, a shallow stream of water containing dissolved oxygen and primary macronutrients is re-circulated past bare plant roots. This direct capillary contact accelerates root nutrient absorption rates by up to 300% compared to typical soil substrates."
        },
        {
          heading: "Water Quality Calibration",
          body: "Keep solution pH levels stabilized between 5.5 and 6.5. Monitor ambient Electrical Conductivity (EC) daily using digital probes to prevent root salt toxicity and leverage maximum ion exchange capacity."
        }
      ]
    },
    {
      id: "rare-exotics",
      title: "Rare Plants",
      category: "Variegated Specimen",
      description: "Acquire and propagate highly coveted exotic and variegated tropical species.",
      iconBg: "bg-purple-50 border-purple-100/60",
      textColor: "text-purple-855",
      renderIcon: () => (
        <svg className="w-7 h-7 text-purple-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M6 3h12l4 6-10 12L2 9z" />
          <path d="M11 3 8 9l4 12" />
          <path d="M13 3l3 6-4 12" />
          <path d="M2 9h20" />
        </svg>
      ),
      detailedContent: [
        {
          heading: "Understanding Chimera Variegation",
          body: "Variegation in species like Monstera Albo rises from genetic mutations halting chlorophyll synthesis in selected cellular sections. These pale tissue zones cannot photosynthesize, requiring growers to offer abundant bright, indirect light to sustain active growth."
        },
        {
          heading: "Exotic Microclimate Fostering",
          body: "Exotic tropical plants are extremely sensitive to cold draft shock and low humidity drops. Preserve stable room temperatures above 21°C and keep cabinet humidity levels strictly between 70% and 85% with continuous airflow."
        }
      ]
    }
  ];

  return (
    <section 
      id="learn-hub" 
      className="py-6 bg-gray-50/30 border-t border-b border-gray-150/40 relative overflow-hidden"
      aria-label="Botanical Learning Hub"
    >
      {/* Backplate subtle structural styling */}
      <div className="absolute top-1/4 right-[5%] w-[500px] h-[500px] bg-green-500/[0.012] rounded-full blur-[130px] pointer-events-none -z-10" />
      <div className="absolute bottom-1/3 left-[4%] w-[400px] h-[400px] bg-emerald-400/[0.015] rounded-full blur-[110px] pointer-events-none -z-10" />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        
        {/* SECTION BADGE & TITLE */}
        <div className="text-center max-w-2xl mx-auto mb-10 space-y-4">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-1.5 px-3 py-1 bg-green-50 border border-green-100 rounded-full text-green-700 text-[10px] font-extrabold uppercase tracking-widest shadow-3xs"
          >
            <BookOpen className="w-3 h-3 text-green-600" />
            <span>Botanical Learning Library</span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.05 }}
            className="text-3xl sm:text-4xl font-[900] text-gray-950 tracking-tight leading-none"
          >
            Learn More About <br className="sm:hidden" /> Plants Every Day
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-sm sm:text-base text-gray-500 font-medium max-w-[650px] mx-auto leading-relaxed"
          >
            Explore clean botanical profiles, plant care guidelines, identification tutorials, and phytology reports compiled by professional botanists.
          </motion.p>
        </div>

        {/* 4x2x1 PROFESSIONAL SaaS DESIGN GRID */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {learningCards.map((card, idx) => (
            <motion.article
              key={card.id}
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: Math.min(idx * 0.04, 0.25) }}
              onClick={() => setSelectedCard(card)}
              className="bg-white rounded-3xl p-6 border border-gray-150 shadow-[0_4px_25px_rgba(0,0,0,0.01)] hover:-translate-y-1.5 hover:shadow-[0_15px_35px_rgba(0,0,0,0.035)] hover:border-gray-250 transition-all duration-300 flex flex-col justify-between cursor-pointer group active:scale-[0.99] h-full"
            >
              <div className="space-y-5">
                {/* SVG Icon holder container */}
                <div 
                  className={`w-14 h-14 rounded-2xl border ${card.iconBg} flex items-center justify-center transition-transform duration-300 group-hover:scale-104 shrink-0`}
                  role="img"
                  aria-label={`${card.title} Icon`}
                >
                  {card.renderIcon()}
                </div>

                <div className="space-y-2">
                  <span className="text-[10px] font-extrabold uppercase tracking-widest text-gray-400">
                    {card.category}
                  </span>
                  <h3 className="text-lg font-black text-gray-950 tracking-tight leading-tight group-hover:text-green-700 transition-colors">
                    {card.title}
                  </h3>
                  <p className="text-xs text-gray-500 font-medium leading-relaxed">
                    {card.description}
                  </p>
                </div>
              </div>

              {/* Call-to-action link */}
              <div className="pt-5 mt-5 border-t border-gray-50 flex items-center text-xs font-bold text-green-700 group-hover:text-green-800 transition-colors">
                <span>Learn More</span>
                <ChevronRight className="w-4 h-4 ml-0.5 group-hover:translate-x-1 transition-transform" />
              </div>
            </motion.article>
          ))}
        </div>
      </div>

      {/* DETAILED INFO DIALOG MODAL VIEW */}
      <AnimatePresence>
        {selectedCard && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-md flex items-center justify-center p-4 z-1000"
            role="dialog"
            aria-modal="true"
            onClick={() => setSelectedCard(null)}
          >
            <motion.div
              initial={{ scale: 0.96, y: 12 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.96, y: 12 }}
              className="bg-white rounded-[32px] overflow-hidden max-w-lg w-full shadow-2xl relative"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header block color */}
              <div className="px-8 pt-8 pb-4 flex items-start justify-between border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-xl border ${selectedCard.iconBg} flex items-center justify-center shrink-0`}>
                    {selectedCard.renderIcon()}
                  </div>
                  <div>
                    <span className="text-[10px] font-black uppercase tracking-widest text-gray-450 block">
                      {selectedCard.category}
                    </span>
                    <h3 className="text-xl font-black text-gray-950 tracking-tight">
                      {selectedCard.title}
                    </h3>
                  </div>
                </div>

                <button
                  onClick={() => setSelectedCard(null)}
                  className="w-8 h-8 rounded-full bg-gray-50 hover:bg-gray-100 text-gray-500 flex items-center justify-center cursor-pointer transition-colors"
                  aria-label="Close learning guide"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Scroller Container */}
              <div className="p-8 space-y-6 max-h-[60vh] overflow-y-auto custom-scrollbar">
                {selectedCard.detailedContent.map((section, idx) => (
                  <div key={idx} className="space-y-2">
                    <h4 className="text-sm font-black text-gray-900 tracking-tight flex items-center gap-1.5 uppercase tracking-wide">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-600" />
                      {section.heading}
                    </h4>
                    <p className="text-xs sm:text-sm text-gray-650 leading-relaxed font-semibold pl-3">
                      {section.body}
                    </p>
                  </div>
                ))}
              </div>

              {/* Footer bar */}
              <div className="px-8 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between text-[11px] text-gray-400 font-extrabold uppercase">
                <span className="flex items-center gap-1">
                  <Globe className="w-3.5 h-3.5 text-gray-400" />
                  Free Educational Resource
                </span>
                <span>PlantIdentifer AI Hub</span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
